import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Hub, HubSection } from '../../types/hub';
import Card from '../ui/Card';
import QuietAction from '../cards/QuietAction';
import SaveToMyOrbconButton from '../saved/SaveToMyOrbconButton';
import { insideWord, plain, resourceWord } from '../../utils/cardFacts';
import { hubPlaceHref } from '../../utils/hubChapter';
import { sectionAnchors } from '../../utils/headingToc';
import { placePath } from '../../utils/placePaths';
import { openPlace, usePlaceOrigin } from '../../utils/placeMotion';

function isNestedSection(title: string) {
  return title.includes(' / ');
}

function sectionResourceCount(section: HubSection, sections: HubSection[], resources: Hub['resources']) {
  const ids = new Set(
    sections
      .filter((item) => item.title === section.title || item.title.startsWith(`${section.title} / `))
      .map((item) => item.id)
  );
  return resources.filter((resource) => resource.sectionId && ids.has(resource.sectionId)).length;
}

export default function ToolkitListRow({
  hub,
  query = '',
  anchorId,
  highlighted = false,
}: {
  hub: Hub;
  query?: string;
  anchorId?: string;
  highlighted?: boolean;
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [opened, setOpened] = useState<boolean | null>(null);
  const to = hubPlaceHref(hub);
  const about = plain(hub.description);
  const program = hub.program?.slug ? hub.program : null;
  const programTo = program ? placePath(program) : '';
  const orderedSections = [...(hub.sections ?? [])].sort((a, b) => a.order - b.order || a.title.localeCompare(b.title));
  const anchors = sectionAnchors(orderedSections);
  const sections = orderedSections.filter((section) => !isNestedSection(section.title));
  const needle = query.trim().toLowerCase();
  const matches = needle
    ? sections.filter((section) => section.title.toLowerCase().includes(needle))
    : [];
  const listOpen = opened ?? matches.length > 0;
  const listed = opened === true || matches.length === 0 ? sections : matches;
  usePlaceOrigin(cardRef, 'hub', hub.slug, 'row');

  useEffect(() => {
    setOpened(null);
  }, [needle]);

  const sectionLink = (section: HubSection) => (
    <li key={section.id} className="border-b border-gray-200">
      <Link
        to={`${to}#${anchors.get(section.id)}`}
        className="flex items-center w-full min-h-[44px] text-left focus-ring"
      >
        <span className="inline-flex flex-wrap items-baseline gap-x-3 min-w-0">
          <span className="text-sm font-medium text-gray-900">{section.title}</span>
          <span className="text-sm text-gray-600">
            {resourceWord(sectionResourceCount(section, hub.sections ?? [], hub.resources ?? []))}
          </span>
        </span>
      </Link>
    </li>
  );

  const openToolkit = () => (
    <QuietAction
      to={to}
      label={`Open ${hub.title}`}
      onClick={(event) => {
        openPlace(event, cardRef.current, { kind: 'hub', slug: hub.slug, via: 'row' }, to, navigate, location);
      }}
    >
      Open
    </QuietAction>
  );

  return (
    <div id={anchorId} className={anchorId ? 'scroll-mt-36' : undefined}>
    <Card ref={cardRef} className={`!p-4 ${highlighted ? '!bg-start-wash !border-l-4 !border-l-start-edge' : ''}`}>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-600">Toolkit</p>
          <h3 className="mt-1 text-lg font-semibold text-gray-900">{hub.title}</h3>
        </div>
        <SaveToMyOrbconButton
          type="HUB"
          hubId={hub.id}
          title={hub.title}
          slug={hub.slug}
          variant="icon"
          align="end"
        />
      </div>
      {program && (
        <p className="mt-2 text-sm">
          <Link
            to={programTo}
            onClick={(event) => {
              openPlace(event, event.currentTarget, { kind: 'program', slug: program.slug }, programTo, navigate, location);
            }}
            className="font-medium text-primary hover:text-primary-dark focus-ring rounded-sm"
          >
            In {program.title}
          </Link>
        </p>
      )}
      {about && <p className="mt-2 text-sm text-gray-600 line-clamp-2">{about}</p>}
      {sections.length === 0 && (
        <p className="mt-2 text-sm text-gray-600">{resourceWord(hub.resources.length)}</p>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2">
        {sections.length > 0 && (
          <button
            type="button"
            className="min-h-[44px] text-sm font-medium text-primary focus-ring rounded-sm"
            aria-expanded={listOpen}
            onClick={() => setOpened(listOpen ? false : true)}
          >
            {listOpen ? 'Hide sections' : insideWord(sections.length, 0)}
          </button>
        )}
        <span className="inline-flex min-h-[44px] items-center">{openToolkit()}</span>
      </div>
      {listOpen && (
        <ul className="mt-2 border-t border-gray-200">
          {listed.map(sectionLink)}
        </ul>
      )}
    </Card>
    </div>
  );
}
